import {
  ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styles from "./DealerInfoTable.module.scss";
import {
  DealerKeysEnum,
  IReportProduct,
  ReportDealerInfoType,
  ReportType,
} from "@/entities/report/types";

import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import {
  setRightPlateChildrenType,
  setRightPlateOpen,
} from "@/app/model/applicationSlice";
import clsx from "clsx";
import {
  setDataToFetch,
  setDataToFetchPreQuantity,
} from "@/entities/report/slice";
import {
  FilialInfoType,
  FilialsTableInfoType,
  MainDealerTotalType,
  ObjectFromHeadersType,
  ProductTotalsType,
  TableInfoType,
} from "../../types/types";
import { getSortIndex } from "../../lib/buildSortIndexMap";
import { ReportProduct } from "./components";

interface DealerInfoTableProps {
  dealer: ReportDealerInfoType;
  reportProductList: IReportProduct[];
  reportStatus: ReportType;
  isDraft: boolean;
  setIsReportChanged: (value: boolean) => void;
  totalView?: boolean;
  isPlanView?: boolean;
  isFilialView?: boolean;
  setCurrentMainDealerId?: (val: string | null) => void;
  currentMainDealerId?: string | null;
  productsDealerData: TableInfoType | null;
  setProductsDealerData: (val: TableInfoType) => void;
  productFilialsData: FilialsTableInfoType | null;
  setProductFilialsData: (val: FilialsTableInfoType) => void;
  reservedData: FilialsTableInfoType | null;
  setReservedData: (val: FilialsTableInfoType) => void;
  productTotals?: ProductTotalsType;
  verticalProductTotals?: ObjectFromHeadersType & {
    productionPlanQuantity: number;
  };
  productDealerFilialTotals: FilialInfoType;
  mainDealerVerticalTotal: MainDealerTotalType;
  sortIndexMap: Map<string, number>;
}

const DealerInfoTableComponent: FC<DealerInfoTableProps> = ({
  dealer,
  totalView = false,
  isPlanView = false,
  isFilialView = false,
  reportProductList,
  reportStatus,
  isDraft,
  setIsReportChanged,
  setCurrentMainDealerId,
  currentMainDealerId = null,
  productsDealerData,
  setProductsDealerData,
  productFilialsData,
  setProductFilialsData,
  productTotals,
  verticalProductTotals,
  reservedData,
  setReservedData,
  productDealerFilialTotals,
  mainDealerVerticalTotal,
  sortIndexMap,
}) => {
  const dispatch = useAppDispatch();
  const ROW_HEIGHT = 50;
  const { isRigthPlateOpen } = useAppSelector((state) => state.app);
  const { dataToFetch, dataToFetchPreQuantity, selectedProductId } =
    useAppSelector((state) => state.reports);
  const [checkChange, setCheckChange] = useState<boolean>(false);

  const showWideTable =
    reportStatus === "confirmed" || reportStatus === "final" || isDraft;

  let exludeDealerKeys: (keyof ReportDealerInfoType)[] = [
    "dealerId",
    "dealerShortName",
    "dealerName",
    "dealerOrderCreated",
    "quantity",
    "orderItemDraftId",
  ];

  if (totalView) {
    exludeDealerKeys = [
      ...exludeDealerKeys,
      "shippedQuantity",
      "reservedQuantity",
    ];
    if (isFilialView) {
      exludeDealerKeys = [...exludeDealerKeys, "requestedQuantity"];
    } else {
      const filtered = exludeDealerKeys.filter(
        (i) => i !== "requestedQuantity",
      );
      exludeDealerKeys = filtered;
    }
  }

  const dealerHeaders = Object.keys(DealerKeysEnum).filter(
    (k) => !exludeDealerKeys.includes(k as keyof ReportDealerInfoType),
  ) as Array<keyof typeof DealerKeysEnum>;
  let headers: Array<
    keyof typeof DealerKeysEnum | "productionPlanQuantity" | "remainsQuantity"
  > = dealerHeaders;

  if (totalView && showWideTable) {
    headers = dealerHeaders.flatMap((item) => {
      if (
        (totalView && item === "requestedQuantity") ||
        item === "reservedQuantity"
      ) {
        return [item, "productionPlanQuantity"];
      }
      return item;
    });
  }
  const hasBranch = !!dealer?.branches?.length;

  if (reportStatus === "requested" || reportStatus === "plan") {
    headers = ["requestedQuantity"];
  }

  if (!totalView && showWideTable) {
    headers = dealerHeaders;
  }

  const dealerMainId =
    currentMainDealerId || dealer?.mainDealerId || dealer?.dealerId;

  useEffect(() => {
    if (!isRigthPlateOpen && currentMainDealerId) {
      setCurrentMainDealerId(null);
    }
  }, [isRigthPlateOpen]);

  const handleBlur = (
    e: ChangeEvent<HTMLInputElement>,
    tempValue: number,
    dealerOrderItem: ReportDealerInfoType,
    hasBranch: boolean,
  ) => {
    e.preventDefault();
    const element = e.target;
    const value = +tempValue <= 99999 ? +tempValue : 99999;
    element.value = value.toString();
    setIsReportChanged(true);
    setCheckChange(!checkChange);

    if (!isFilialView && hasBranch) {
      const newReservedObj = { ...reservedData };
      newReservedObj[element.id][dealerOrderItem.dealerId] = +value / 1;
      setReservedData(newReservedObj);
      const newArr = dataToFetchPreQuantity.filter(
        (item) =>
          !(
            item.mainDealerId === dealerOrderItem.mainDealerId &&
            item.vendorId === e.target.id
          ),
      );
      dispatch(
        setDataToFetchPreQuantity([
          ...newArr,
          {
            mainDealerId: dealerOrderItem.dealerId,
            quantity: value,
            vendorId: e.target.id,
          },
        ]),
      );
    } else {
      const newObj = { ...productsDealerData };
      const newFilialObj = { ...productFilialsData };
      newObj[element.id][dealerOrderItem.dealerId].quantity = +value / 1;
      newFilialObj[element.id][dealerOrderItem.dealerId] = +value;

      setProductFilialsData(newFilialObj);
      setProductsDealerData(newObj);
      const newArr = dataToFetch.filter(
        (item) => item.orderItemDraftId !== element.dataset.orderitemdraftid,
      );

      dispatch(
        setDataToFetch([
          ...newArr,
          {
            orderItemDraftId: element.dataset.orderitemdraftid,
            quantity: value,
          },
        ]),
      );
    }

    if (!element.value) element.value = "0";
  };

  const showFilialHandler = () => {
    setCurrentMainDealerId(dealer.dealerId);
    dispatch(setRightPlateOpen(true));
    dispatch(setRightPlateChildrenType("dealerFilials"));
  };

  const getMainDealerTotalValue = useCallback(
    (
      productId: string,
      dealerItem: ReportDealerInfoType,
      productData: TableInfoType | null,
      keyName: keyof ReportDealerInfoType,
    ) => {
      if (
        !productData ||
        !productData[productId] ||
        (keyName !== "requestedQuantity" &&
          keyName !== "reservedQuantity" &&
          keyName !== "shippedQuantity")
      ) {
        return 0;
      }

      const branches = dealerItem.branches ?? [];

      return [dealerItem, ...branches].reduce((acc, branch) => {
        const branchData = productData[productId]?.[branch.dealerId];
        if (!branchData) {
          return acc;
        }
        return acc + +(branchData[keyName] ?? 0);
      }, 0);
    },
    [dealer],
  );

  const getTotalForOneDealer = (
    productsData: TableInfoType,
    dealerId: string,
    type: keyof typeof DealerKeysEnum,
  ): number => {
    let value = 0;

    if (productsData) {
      for (const i in productsData) {
        if (
          type !== "orderItemDraftId" &&
          productsData[i][dealerId] &&
          productsData[i][dealerId][type] >= 0
        )
          value += productsData[i][dealerId][type];
      }
    }
    return value;
  };

  const headerTotals = useMemo(() => {
    const totals: Record<string, number> = {};

    if (!productsDealerData) {
      return totals;
    }

    for (const el of headers) {
      totals[el] = getTotalForOneDealer(
        productsDealerData,
        dealer.dealerId,
        el as keyof typeof DealerKeysEnum,
      );
    }

    totals.quantity = getTotalForOneDealer(
      productsDealerData,
      dealer.dealerId,
      "quantity",
    );
    totals.ensuredQuantity = getTotalForOneDealer(
      productsDealerData,
      dealer.dealerId,
      "ensuredQuantity",
    );

    return totals;
  }, [productsDealerData, dealer.dealerId, headers]);

  if (!dealer) return <></>;

  return (
    <table
      key={dealer.dealerId}
      className={clsx(styles.infoTable, { [styles.fixed]: totalView })}
    >
      <thead>
        {!isFilialView && (
          <tr className={styles.tableHeaderTopLine}>
            {productsDealerData &&
              headers.map((el, idx) => {
                let totalVert = headerTotals[el] ?? 0;

                if (
                  (el === "reservedQuantity" ||
                    el === "shippedQuantity" ||
                    el === "requestedQuantity") &&
                  hasBranch
                ) {
                  totalVert = mainDealerVerticalTotal?.[dealerMainId]?.[el];
                }
                const oneDealerTotal = isDraft
                  ? headerTotals.quantity
                  : headerTotals.ensuredQuantity;
                const totalRightValue =
                  hasBranch && isDraft
                    ? mainDealerVerticalTotal?.[dealerMainId]?.quantity
                    : oneDealerTotal;
                if (el === "ensuredQuantity") {
                  return (
                    <th
                      className={styles.totalRow}
                      key={el + dealer.dealerId + idx}
                    >
                      {totalView && verticalProductTotals ? (
                        <>
                          {verticalProductTotals.productionPlanQuantity}
                          {isDraft && (
                            <>
                              /
                              <span
                                className={
                                  verticalProductTotals.quantity !==
                                  verticalProductTotals.productionPlanQuantity
                                    ? styles.redValue
                                    : styles.greenValue
                                }
                              >
                                {verticalProductTotals?.quantity}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {verticalProductTotals?.productionPlanQuantity}/
                          {totalRightValue}
                        </>
                      )}
                    </th>
                  );
                }
                return (
                  <th
                    className={styles.totalRow}
                    key={el + idx + dealer.dealerId}
                  >
                    {totalView && verticalProductTotals
                      ? verticalProductTotals[el as keyof ObjectFromHeadersType]
                      : totalVert}
                  </th>
                );
              })}
          </tr>
        )}
        {showWideTable && (
          <tr>
            <th className={styles.nameCell} colSpan={headers.length}>
              {totalView ? (
                isFilialView ? (
                  `Итого по дилеру`
                ) : (
                  "Итого"
                )
              ) : (
                <div className={styles.filialBlock}>
                  <p>
                    {isFilialView
                      ? `Филиал «${dealer.dealerShortName}»`
                      : dealer.dealerShortName}
                  </p>

                  {!currentMainDealerId && hasBranch && !isRigthPlateOpen && (
                    <p
                      className={styles.filialText}
                      onClick={showFilialHandler}
                    >
                      Филиалы
                    </p>
                  )}
                </div>
              )}
            </th>
          </tr>
        )}
        <tr>
          {headers.map((el, idx) => {
            if (showWideTable) {
              return (
                <th
                  className={styles.columnNameCell}
                  key={idx + idx + el + idx}
                >
                  {totalView && el === "productionPlanQuantity"
                    ? "Будет произведено"
                    : DealerKeysEnum[el as keyof typeof DealerKeysEnum]}
                </th>
              );
            } else {
              return (
                <th
                  className={styles.columnNameCell}
                  key={idx + idx + el + idx}
                >
                  {isPlanView ? "Будет произведено" : dealer.dealerShortName}
                </th>
              );
            }
          })}
        </tr>
      </thead>
      <tbody
        style={{
          position: "relative",
          display: "block",
          height: reportProductList.length * ROW_HEIGHT,
        }}
      >
        {reportProductList.map((product) => (
          <ReportProduct
            key={product.vendorId}
            product={product}
            dealer={dealer}
            totalView={totalView}
            isFilialView={isFilialView}
            reportStatus={reportStatus}
            isDraft={isDraft}
            productsDealerData={productsDealerData}
            productTotals={productTotals}
            reservedData={reservedData}
            productDealerFilialTotals={productDealerFilialTotals}
            dealerMainId={dealerMainId}
            hasBranch={hasBranch}
            headers={headers}
            sortIndex={getSortIndex(sortIndexMap, product.vendorId)}
            selectedProductId={selectedProductId}
            getMainDealerTotalValue={getMainDealerTotalValue}
            handleBlur={handleBlur}
          />
        ))}
      </tbody>
    </table>
  );
};

export const DealerInfoTable = memo(DealerInfoTableComponent);
